import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { invalidateEmailAttachmentTableQuery } from "hooks/useEmailAttachmentTable";
import EmailAttachmentsService from "services/EmailAttachments";
import EmailTemplateService from "services/EmailTemplate";

export const EMAIL_ATTACHMENT_QUERY_KEY = "email-attachment";

export const useEmailAttachment = id =>
  useQuery({
    queryKey: [EMAIL_ATTACHMENT_QUERY_KEY, id],
    queryFn: () => EmailAttachmentsService.getEmailAttachment(id),
    enabled: !!id,
    retry: false,
  });

/**
 * Creates an email attachment, or updates one when `id` is given, and
 * refreshes the attachments table and the saved record's cache entry.
 */
export const useSaveEmailAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) =>
      id
        ? EmailAttachmentsService.updateEmailAttachment(id, data)
        : EmailAttachmentsService.createEmailAttachment(data),
    onSuccess: async (_, { id }) => {
      await invalidateEmailAttachmentTableQuery();
      if (id) {
        await queryClient.invalidateQueries({
          queryKey: [EMAIL_ATTACHMENT_QUERY_KEY, String(id)],
        });
      }
    },
  });
};

export const useDeleteEmailAttachment = () =>
  useMutation({
    mutationFn: id => EmailAttachmentsService.deleteEmailAttachment(id),
    onSuccess: invalidateEmailAttachmentTableQuery,
  });

/** Merge tags and special links offered by the Unlayer editor. */
export const useUnlayerMergeData = () => {
  const { data: mergeTags = {} } = useQuery({
    queryKey: ["email-template-merge-tags"],
    queryFn: EmailTemplateService.getTags,
  });
  const { data: specialLinks = {} } = useQuery({
    queryKey: ["email-template-special-links"],
    queryFn: EmailTemplateService.getUrls,
  });
  return { mergeTags, specialLinks };
};
